/**
 * periodVariation.ts — Factor determinista de variación por periodo (demo).
 *
 * QUÉ HACE: cuando el usuario navega ‹ › entre periodos en las tarjetas del
 *   Resumen, el modo demo necesita que los datos cambien por periodo sin perder
 *   el determinismo. Este helper devuelve un factor 0.85–1.15 derivado del hash
 *   FNV-1a de `tipo | periodo | inicio de ventana`, para escalar las series demo
 *   (horas pico, días de la semana, matrices y velas).
 *
 * QUÉ INVOCA: `resolvePeriodWindow` (ventana calendario) e `isoDay`.
 * QUÉ EXPORTA: periodVariationFactor, scalePeriodValues.
 * QUÉ NO DEBE HACER: UI, storage ni fetch; es un cálculo puro.
 */
import {
  resolvePeriodWindow,
  type ChartWindowPeriod,
} from "@/lib/utils/periodWindows";

/** Hash determinista (FNV-1a). */
function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Fecha YYYY-MM-DD local (clave de la ventana). */
function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * Factor determinista dentro de [min, max] para un tipo de serie y una ventana
 * de periodo. El mismo periodo devuelve siempre el mismo factor.
 */
export function periodVariationFactor(
  kind: string,
  period: ChartWindowPeriod,
  referenceDate: Date,
  min = 0.85,
  max = 1.15,
): number {
  const window = resolvePeriodWindow(period, referenceDate);
  const seed = `${kind}|${period}|${isoDay(window.from)}`;
  const t = (hash(seed) % 1000) / 1000;
  return min + t * (max - min);
}

/** Escala los valores numéricos indicados de una fila por el factor. */
export function scalePeriodValues<T extends Record<string, unknown>>(
  row: T,
  keys: readonly string[],
  factor: number,
): T {
  const scaled: Record<string, unknown> = { ...row };
  keys.forEach((key) => {
    const value = row[key];
    if (typeof value === "number") scaled[key] = Math.round(value * factor);
  });
  return scaled as T;
}
